import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { fetchHeycoachSubscriptionDocDetailedForUserId } from "../lib/heycoachSubscription.js";
import {
  fetchConnectStatus,
  fetchPlansViaGateway,
} from "../lib/stripe.js";
import { findTierInCatalog } from "../lib/planCatalog.js";
import { useAuthUserId } from "../hooks/useAuthUserId.js";
import { useRole } from "../hooks/useRole.js";
import { toUserFacingBillingError } from "../lib/userFacingErrors.js";

const CoachBillingContext = createContext(null);

const EMPTY_PLANS_SLICE = { plans: [], isLoading: false, error: null, catalogHint: null };

function audienceKey(audience) {
  return audience ? String(audience).trim() : "all";
}

export function CoachBillingProvider({ children }) {
  const { userId, authLoading } = useAuthUserId();
  const role = useRole();
  const isCoach = role === "coach";

  const [subscription, setSubscription] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [subscriptionError, setSubscriptionError] = useState(null);

  const [connect, setConnect] = useState(null);
  const [connectLoading, setConnectLoading] = useState(true);
  const [connectError, setConnectError] = useState(null);

  const [plansByAudience, setPlansByAudience] = useState({});
  const plansStatusRef = useRef({});
  const prevUserIdRef = useRef(userId);

  useEffect(() => {
    if (prevUserIdRef.current === userId) return;
    prevUserIdRef.current = userId;
    setPlansByAudience({});
    plansStatusRef.current = {};
  }, [userId]);

  const resetCoachOnlyState = useCallback(() => {
    setSubscription(null);
    setSubscriptionLoading(false);
    setSubscriptionError(null);
    setConnect(null);
    setConnectLoading(false);
    setConnectError(null);
  }, []);

  const refetchSubscription = useCallback(async ({ silent = false } = {}) => {
    if (!userId || !isCoach) {
      setSubscription(null);
      setSubscriptionError(null);
      setSubscriptionLoading(false);
      return;
    }
    if (!silent) setSubscriptionLoading(true);
    setSubscriptionError(null);
    try {
      const { doc, loadError } =
        await fetchHeycoachSubscriptionDocDetailedForUserId(userId);
      setSubscription(doc);
      setSubscriptionError(loadError);
    } catch (e) {
      setSubscription(null);
      setSubscriptionError(toUserFacingBillingError(e?.message ?? String(e), {
        fallback: "We couldn't load your subscription right now. Please refresh the page.",
      }));
    } finally {
      setSubscriptionLoading(false);
    }
  }, [userId, isCoach]);

  const refetchConnect = useCallback(async ({ silent = false } = {}) => {
    if (!userId || !isCoach) {
      setConnect(null);
      setConnectError(null);
      setConnectLoading(false);
      return;
    }
    if (!silent) setConnectLoading(true);
    setConnectError(null);
    try {
      const data = await fetchConnectStatus();
      setConnect(data.connect ?? null);
    } catch (e) {
      setConnectError(
        toUserFacingBillingError(e?.message ?? String(e), {
          fallback: "We couldn't load payout settings right now. Please try again from Billing.",
        }),
      );
      setConnect(null);
    } finally {
      setConnectLoading(false);
    }
  }, [userId, isCoach]);

  const refetchPlans = useCallback(async (audience, { silent = false } = {}) => {
    const key = audienceKey(audience);
    if (!userId) {
      setPlansByAudience((prev) => ({ ...prev, [key]: EMPTY_PLANS_SLICE }));
      return;
    }
    plansStatusRef.current[key] = "loading";
    if (!silent) {
      setPlansByAudience((prev) => ({
        ...prev,
        [key]: { ...(prev[key] ?? EMPTY_PLANS_SLICE), isLoading: true, error: null },
      }));
    }
    try {
      const data = await fetchPlansViaGateway(audience ? { audience } : {});
      const plans = Array.isArray(data.plans) ? data.plans : [];
      const catalogHint = data.catalogHint ?? null;
      setPlansByAudience((prev) => ({
        ...prev,
        [key]: { plans, isLoading: false, error: null, catalogHint },
      }));
    } catch (e) {
      const msg = toUserFacingBillingError(e?.message ?? String(e), {
        fallback: "We couldn't load plans right now. Please refresh the page.",
      });
      setPlansByAudience((prev) => ({
        ...prev,
        [key]: { plans: [], isLoading: false, error: msg },
      }));
    } finally {
      plansStatusRef.current[key] = "done";
    }
  }, [userId]);

  const ensurePlans = useCallback((audience) => {
    const key = audienceKey(audience);
    if (!userId) return;
    const status = plansStatusRef.current[key];
    if (status === "loading" || status === "done") return;
    plansStatusRef.current[key] = "loading";
    refetchPlans(audience).finally(() => {
      plansStatusRef.current[key] = "done";
    });
  }, [userId, refetchPlans]);

  const refetchAll = useCallback(async () => {
    await Promise.all([
      refetchSubscription(),
      refetchConnect(),
      refetchPlans("coach"),
      refetchPlans(undefined),
    ]);
  }, [refetchSubscription, refetchConnect, refetchPlans]);

  useEffect(() => {
    if (authLoading || role === "loading") return;
    if (!userId || !isCoach) {
      resetCoachOnlyState();
      return;
    }
    refetchSubscription();
    refetchConnect();
  }, [userId, isCoach, authLoading, role, resetCoachOnlyState, refetchSubscription, refetchConnect]);

  const value = useMemo(
    () => ({
      subscription,
      subscriptionLoading,
      subscriptionError,
      refetchSubscription,
      connect,
      connectLoading,
      connectError,
      refetchConnect,
      plansByAudience,
      ensurePlans,
      refetchPlans,
      refetchAll,
    }),
    [
      subscription,
      subscriptionLoading,
      subscriptionError,
      refetchSubscription,
      connect,
      connectLoading,
      connectError,
      refetchConnect,
      plansByAudience,
      ensurePlans,
      refetchPlans,
      refetchAll,
    ],
  );

  return (
    <CoachBillingContext.Provider value={value}>
      {children}
    </CoachBillingContext.Provider>
  );
}

export function useCoachBilling() {
  const ctx = useContext(CoachBillingContext);
  if (!ctx) {
    throw new Error("useCoachBilling must be used within CoachBillingProvider");
  }
  return ctx;
}

export function usePlanCatalogFromContext(audience) {
  const { plansByAudience, ensurePlans, refetchPlans } = useCoachBilling();
  const key = audienceKey(audience);

  useEffect(() => {
    ensurePlans(audience);
  }, [audience, ensurePlans]);

  const slice = plansByAudience[key] ?? { plans: [], isLoading: true, error: null, catalogHint: null };

  const findTier = useCallback(
    (planId) => findTierInCatalog(slice.plans, planId),
    [slice.plans],
  );

  return {
    plans: slice.plans,
    isLoading: slice.isLoading,
    error: slice.error,
    catalogHint: slice.catalogHint,
    refetch: () => refetchPlans(audience),
    findTier,
  };
}
